import { useState } from 'react';
import { Zap, Droplets, Utensils, Home, TreePine, Monitor, Briefcase, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/translations';
import { getServicePricing } from '../translations/servicePricing';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const ServicesSection = () => {
  const { language } = useLanguage();
  const t = translations[language].services;
  const [selectedService, setSelectedService] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const modalT = translations[language].modal;

  const services = [
    {
      key: 'electrical',
      icon: Zap,
      title: t.electrical.title,
      description: t.electrical.description,
      items: t.electrical.items,
      phone: '06/70-237-8089',
      phoneLink: 'tel:+36702378089'
    },
    {
      key: 'plumbing',
      icon: Droplets,
      title: t.plumbing.title,
      description: t.plumbing.description,
      items: t.plumbing.items,
      phone: '06/70-237-8089',
      phoneLink: 'tel:+36702378089'
    },
    {
      key: 'kitchen',
      icon: Utensils,
      title: t.kitchen.title,
      description: t.kitchen.description,
      items: t.kitchen.items,
      phone: '06/70-237-8089',
      phoneLink: 'tel:+36702378089'
    },
    {
      key: 'renovation',
      icon: Home,
      title: t.renovation.title,
      description: t.renovation.description,
      items: t.renovation.items,
      phone: '06/70-237-8089',
      phoneLink: 'tel:+36702378089'
    },
    {
      key: 'gardening',
      icon: TreePine,
      title: t.gardening.title,
      description: t.gardening.description,
      items: t.gardening.items,
      phone: '06/70-237-8089',
      phoneLink: 'tel:+36702378089'
    },
    {
      key: 'it',
      icon: Monitor,
      title: t.it.title,
      description: t.it.description,
      items: t.it.items,
      phone: '06/70-270-1639',
      phoneLink: 'tel:+36702701639'
    },
    {
      key: 'digital',
      icon: Briefcase,
      title: t.digital.title,
      description: t.digital.description,
      items: t.digital.items,
      phone: '06/70-664-6973',
      phoneLink: 'tel:+36706646973',
      unavailable: true
    }
  ];

  const openServiceDetails = (service) => {
    if (service.unavailable) return;
    setSelectedService(service);
    setIsDialogOpen(true);
  };

  return (
    <section id="szolgaltatasok" className="py-24 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-[#7B2F3D] font-semibold text-sm uppercase tracking-widest">{t.badge}</span>
          <h2 className="text-4xl font-bold mt-3 text-gray-900">
            {t.title}
          </h2>
          <p className="text-gray-600 mt-4">
            {t.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => (
            <div
              key={service.key}
              onClick={() => openServiceDetails(service)}
              data-testid={`service-card-${service.key}`}
              className={`group bg-white rounded-xl p-7 shadow-sm border border-gray-200 transition-all duration-300 relative ${
                service.unavailable
                  ? 'opacity-60 cursor-not-allowed'
                  : 'hover:shadow-lg hover:border-[#8B3A3A] cursor-pointer'
              }`}
            >
              {service.unavailable && (
                <span
                  data-testid={`service-unavailable-badge-${service.key}`}
                  className="absolute top-4 right-4 bg-[#7B2F3D] text-white text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full"
                >
                  {modalT.unavailable}
                </span>
              )}
              <div className={`w-12 h-12 rounded-lg bg-[#7B2F3D]/10 flex items-center justify-center mb-5 transition-colors ${
                service.unavailable ? '' : 'group-hover:bg-[#7B2F3D]'
              }`}>
                <service.icon className={`w-6 h-6 text-[#7B2F3D] transition-colors ${
                  service.unavailable ? '' : 'group-hover:text-white'
                }`} />
              </div>
              <h3 className="font-bold text-lg text-gray-900 mb-3">{service.title}</h3>
              <ul className="space-y-2 mb-4">
                {service.items.map((item) => (
                  <li key={item} className="text-sm text-gray-600 flex gap-2">
                    <span className="text-[#7B2F3D] mt-1">•</span>
                    {item}
                  </li>
                ))}
              </ul>
              {!service.unavailable && (
                <div className="text-sm text-[#7B2F3D] font-semibold group-hover:text-[#8B3A3A]">
                  {t.detailsLink}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 p-6 rounded-xl bg-[#7B2F3D]/5 border border-blue-200 text-center">
          <p className="text-gray-700">
            <strong className="text-gray-900">{t.calloutFee}</strong> {t.calloutText}{' '}
            <strong className="text-[#7B2F3D]">{t.calloutPrice}</strong> {t.calloutNote}
          </p>
        </div>
      </div>

      {/* Service Details Modal */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {selectedService && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4 mb-2">
                  <div className="w-14 h-14 rounded-lg bg-[#7B2F3D] flex items-center justify-center flex-shrink-0">
                    <selectedService.icon className="w-8 h-8 text-white" />
                  </div>
                  <DialogTitle className="text-2xl font-bold text-gray-900">
                    {selectedService.title}
                  </DialogTitle>
                </div>
                <DialogDescription className="text-base text-gray-600 leading-relaxed pt-2">
                  {selectedService.description}
                </DialogDescription>
              </DialogHeader>

              <div className="mt-6">
                <h4 className="text-lg font-bold text-gray-900 mb-4">{modalT.servicesTitle}</h4>
                <ul className="space-y-2 mb-6">
                  {selectedService.items.map((item) => (
                    <li key={item} className="text-gray-600 flex gap-3">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="border-t pt-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">{modalT.pricingTitle}</h4>
                  <div className="space-y-3">
                    {getServicePricing(selectedService.key, language).map((price) => (
                      <div key={price.service} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                        <h5 className="font-semibold text-gray-900">{price.service}</h5>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6 flex gap-3">
                  <a
                    href="#idopontfoglalas"
                    onClick={() => setIsDialogOpen(false)}
                    className="flex-1 px-6 py-3 bg-[#7B2F3D] text-white rounded-lg font-semibold hover:bg-[#6A2835] transition-colors text-center"
                  >
                    {modalT.bookingBtn}
                  </a>
                  <a
                    href={selectedService.phoneLink}
                    className="flex-1 px-6 py-3 bg-white border-2 border-[#7B2F3D] text-[#7B2F3D] rounded-lg font-semibold hover:bg-[#7B2F3D]/5 transition-colors text-center"
                  >
                    {selectedService.phone}
                  </a>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ServicesSection;
